// Saving workspace settings: PUT /api/workspace/doc (api/awsClient.js saveData).
// The server merges the settings it receives over the stored ones key by key
// ({ ...stored, ...incoming }, every sanitizer guarded by "was this key sent"),
// so a save changes only the keys it carries.
//
// A patch of { pilot } from a page other than the Control Room changes
// settings.pilot and nothing else. The caller also updates the App-level state
// for those keys, so the Control Room, which hydrates from that state, sees the
// new value without a reload.
import { saveData } from "../api/awsClient";

/** Keys a patch may carry: top-level settings a non-Control Room page owns. */
export const PATCHABLE_SETTINGS = ["pilot"];

/**
 * What a Control Room save carries: only what this window changed since its last
 * save landed (`lastSaved`, that snapshot as JSON). A window's copy is as old as
 * the moment it loaded, so sending all of it would put back everything that has
 * changed elsewhere since: a tool connected in another window would read as
 * disconnected, and its chosen pipeline, a teammate added or a mode changed
 * would be undone. A value of undefined never reaches the server. With no last
 * save to compare against, everything goes.
 */
export function changedSettings(lastSaved, next) {
  let before = null;
  try {
    before = JSON.parse(lastSaved);
  } catch {}
  if (!before || typeof before !== "object") return next;
  const changed = {};
  for (const [key, value] of Object.entries(next || {})) {
    if (value === undefined) continue;
    if (JSON.stringify(value) !== JSON.stringify(before[key])) changed[key] = value;
  }
  return changed;
}

/**
 * The Team directory as of this window's last save (`lastSaved`, that snapshot as JSON), or null
 * with none. A directory edit is sent with it, so the server applies only what this window
 * changed to the list as stored now.
 */
export function directoryBaseOf(lastSaved) {
  try {
    const base = JSON.parse(lastSaved)?.teamMembers;
    return Array.isArray(base) ? base : null;
  } catch {
    return null;
  }
}

/**
 * Saves `patch` ({ key: value } for PATCHABLE_SETTINGS keys only). Throws on
 * a refused or failed save, and when `docReady` is false: a workspace
 * document that did not load is never written over.
 */
export async function saveWorkspaceSettingsPatch(patch, { docReady = true } = {}) {
  if (!docReady) throw new Error("workspace_doc_unavailable");
  const settings = {};
  for (const [key, value] of Object.entries(patch || {})) {
    if (!PATCHABLE_SETTINGS.includes(key)) {
      throw new Error(`setting_not_patchable:${key}`);
    }
    settings[key] = value === undefined ? null : value;
  }
  if (!Object.keys(settings).length) return;
  await saveData(null, { updatedAt: new Date().toISOString(), settings });
}
